/** The one Needs panel (SA-1196, spec §5.4): the value, the stale mark, the
 * restricted word and the source line, worded once. `TaskForm` and the
 * Compass build-cost view mount it. */
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { FactList, type Fact } from '@/components/records/FactList'
import type { TaskFieldRead } from '@/types/process'
import { FieldValue } from './FieldValue'
import { STALE_WORDS, sourceLine } from './fieldStateText'
import { RESTRICTED_VALUE } from './restrictedText'

/** A need's source line is shown only for a known writer: an `unknown` source
 * (a value from before stamping, on every in-flight record) shows none here,
 * where "pre-filled" would describe nothing. `FieldBlock` keeps it on a control. */
function shownSource(n: TaskFieldRead) {
  return n.source && !n.redacted && n.source.kind !== 'unknown' ? n.source : null
}

export function NeedsList({ needs, className, heading = 'Needs' }: { needs: TaskFieldRead[]; className?: string; heading?: string }) {
  const zone = useDefaultTimezone()
  if (needs.length === 0) return null
  return (
    <section aria-label={heading} className={className ?? 'rounded-xl border border-border bg-muted/30 p-3 text-sm'}>
      <h4 className="text-xs uppercase tracking-wide text-muted-foreground">{heading}</h4>
      <FactList
        className="mt-1"
        facts={needs.map((n): Fact => {
          const source = shownSource(n)
          return {
            key: n.id,
            label: n.label,
            wide: n.type === 'table',
            value: n.redacted ? <span className="text-muted-foreground">{RESTRICTED_VALUE}</span> : <FieldValue field={n} />,
            after: (n.stale || source) ? (
              <span className="ml-2 text-xs">
                {n.stale && <span className="text-warning-strong">{STALE_WORDS}</span>}
                {n.stale && source && <span className="text-muted-foreground"> · </span>}
                {source && <span className="text-muted-foreground">{sourceLine(source, new Date(), zone)}</span>}
              </span>
            ) : undefined,
          }
        })}
      />
    </section>
  )
}
